import { useEffect, useState } from "react";

import { api, errMsg } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { useToast } from "../../../context/ToastContext.js";
import { Button } from "../../../components/ui/button.js";
import { Textarea } from "../../../components/ui/textarea.js";
import { useConfirm } from "../../../components/ui/confirm-dialog.js";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../../components/ui/dialog.js";
import { DropdownSelect } from "../../../components/DropdownButton.js";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../../components/ui/table.js";
import {
  AssignmentStatusBadge, DeleteIconButton, EditIconButton, EmptyState, Field, SectionCard, fmtDate,
} from "../shared.js";
import {
  ASSIGNMENT_STATUS_LABELS, type AssignedGoalRow, type AssignmentStatus, type ReviewDetail,
} from "../types.js";

// Spec 4.B: goals from the organization/manager goals catalog, assigned to this
// employee for this fiscal year. Distinct from the in-review goals, which live
// inside the period accordions.
export default function AssignedGoalsSection({
  review,
  onChanged,
}: {
  review: ReviewDetail;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const caps = review.capabilities;
  // Rows are read-only; Edit opens a dialog with status and comment, as in the
  // Skills section.
  const [editing, setEditing] = useState<AssignedGoalRow | null>(null);

  const [comment, setComment] = useState(review.assignedGoalsComment ?? "");
  const [savingSummary, setSavingSummary] = useState(false);

  async function removeAssignment(goal: AssignedGoalRow) {
    const ok = await confirm({
      title: "Remove this goal?",
      description: `"${goal.title}" will be unassigned from this employee, and its status and comment on this review are lost.`,
      actionText: "Remove",
      tone: "destructive",
    });
    if (!ok) return;
    const assignmentId = goal.assignmentId;
    try {
      await api.delete(`/org-goal-assignments/${assignmentId}`);
      toast.show("Assignment removed", "success");
      onChanged();
    } catch (e) {
      logError("AssignedGoalsSection.remove", e);
      toast.show(errMsg(e) ?? "Could not remove the assignment", "error");
    }
  }

  async function saveSummary() {
    setSavingSummary(true);
    try {
      await api.put(`/performance/reviews/${review.id}/assigned-goals-summary`, {
        assignedGoalsComment: comment || null,
      });
      toast.show("Summary saved", "success");
      onChanged();
    } catch (e) {
      logError("AssignedGoalsSection.saveSummary", e);
      toast.show(errMsg(e) ?? "Could not save the summary", "error");
    } finally {
      setSavingSummary(false);
    }
  }

  return (
    <SectionCard
      title="Assigned goals"
      collapsible
      description={`Goals assigned to this employee for ${review.fiscalYearLabel}.`}
    >
      {review.assignedGoals.length === 0 ? (
        <EmptyState message="No organization or manager goals are assigned for this fiscal year." />
      ) : (
        <div className="overflow-x-auto rounded-md border border-surface-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-3">Goal</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Manager comment</TableHead>
                <TableHead>Assigned by</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead className="pr-3 text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {review.assignedGoals.map((g) => (
                <TableRow key={g.assignmentId}>
                  <TableCell className="pl-3 font-medium">{g.title}</TableCell>
                  <TableCell>
                    <AssignmentStatusBadge status={g.status} />
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <span className="whitespace-pre-wrap">
                      {g.comment || <span className="text-surface-400">—</span>}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{g.assignedByName ?? "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-surface-500">{fmtDate(g.assignedAt)}</TableCell>
                  <TableCell className="pr-3 text-center">
                    {caps.canEditAssignedGoals && (
                      <div className="flex items-center justify-center gap-0.5">
                        <EditIconButton name={g.title} onClick={() => setEditing(g)} />
                        <DeleteIconButton name={g.title} onClick={() => void removeAssignment(g)} />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <EditAssignedGoalDialog goal={editing} onClose={() => setEditing(null)} onSaved={onChanged} />

      {/* The summary is saved even with no assigned goals, per the spec's empty state. */}
      <div className="mt-5 border-t border-surface-100 pt-5">
        <Field label="Manager comment on assigned goals">
          {caps.canEditAssignedGoalsSummary ? (
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
          ) : (
            <p className="text-[13px] whitespace-pre-wrap">
              {review.assignedGoalsComment || <span className="text-surface-400">—</span>}
            </p>
          )}
        </Field>
      </div>

      {caps.canEditAssignedGoalsSummary && (
        <div className="mt-3 flex justify-end">
          <Button size="sm" disabled={savingSummary} onClick={() => void saveSummary()}>
            {savingSummary ? "Saving…" : "Save summary"}
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

function EditAssignedGoalDialog({
  goal,
  onClose,
  onSaved,
}: {
  goal: AssignedGoalRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [openDropdown, setOpenDropdown] = useState(false);
  const [status, setStatus] = useState<string>("NONE");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!goal) return;
    setStatus(goal.status ?? "NONE");
    setComment(goal.comment ?? "");
    setError("");
  }, [goal]);

  async function save() {
    if (!goal) return;
    setSaving(true);
    setError("");
    try {
      await api.patch(`/org-goal-assignments/${goal.assignmentId}`, {
        status: status === "NONE" ? null : status,
        comment: comment.trim() || null,
      });
      toast.show("Goal updated", "success");
      onSaved();
      onClose();
    } catch (e) {
      logError("AssignedGoalsSection.update", e);
      setError(errMsg(e) ?? "Could not update the goal");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={goal != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit: {goal?.title}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <Field label="Status">
            <DropdownSelect
              value={status}
              onChange={setStatus}
              options={[
                { value: "NONE", label: "Not set" },
                ...(Object.keys(ASSIGNMENT_STATUS_LABELS) as AssignmentStatus[])
                  .map((st) => ({ value: st, label: ASSIGNMENT_STATUS_LABELS[st] })),
              ]}
              placeholder="Not set"
              ariaLabel="Goal status"
              clearable={false}
              className="w-full justify-between"
              open={openDropdown}
              onToggle={() => setOpenDropdown((o) => !o)}
              onClose={() => setOpenDropdown(false)}
            />
          </Field>
          <Field label="Comment">
            <Textarea value={comment} maxLength={4000} onChange={(e) => setComment(e.target.value)} />
          </Field>
          {error && <p className="text-[13px] text-[var(--color-error-fg)]">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving} onClick={() => void save()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
